import { ChevronRight } from 'lucide-react';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { NavLink, matchPath, useLocation } from 'react-router-dom';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from '@column/ui';

export interface NavSubItem {
  title: string;
  path: string;
  activePath?: string;
}

export interface NavItem {
  title: string;
  path: string;
  activePath?: string;
  icon: ReactNode;
  exact?: boolean;
  items?: NavSubItem[];
  /** Show a horizontal separator after this item */
  separatorAfter?: boolean;
}

export interface NavMainProps {
  items: NavItem[];
}

const isSubItemActive = (pathname: string, item: NavSubItem): boolean => {
  const paths = [item.path, item.activePath, item.activePath ? `${item.activePath}/*` : undefined].filter(
    Boolean
  ) as string[];
  return paths.some((p) => matchPath(p, pathname));
};

const isSectionActive = (pathname: string, item: NavItem): boolean => {
  const base = item.activePath ?? item.path;
  if (item.exact) {
    return !!matchPath(base, pathname);
  }
  return !!matchPath(base, pathname) || !!matchPath(`${base}/*`, pathname);
};

const shallowEqualBoolMap = (a: Record<string, boolean>, b: Record<string, boolean>): boolean => {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

const NavMainImpl: React.FC<NavMainProps> = ({ items }) => {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const autoOpenSections = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const item of items) {
      if (item.items && isSectionActive(location.pathname, item)) {
        map[item.title] = true;
      }
    }
    return map;
  }, [items, location.pathname]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(autoOpenSections);

  useEffect(() => {
    setOpenSections((prev) => (shallowEqualBoolMap(prev, autoOpenSections) ? prev : autoOpenSections));
  }, [autoOpenSections]);

  return (
    <SidebarGroup>
      <SidebarMenu className="gap-0">
        {items.map((item) => {
          const active = isSectionActive(location.pathname, item);
          const hasChildren = item.items && item.items.length > 0;

          return (
            <React.Fragment key={item.title}>
              <Collapsible
                asChild
                open={openSections[item.title] ?? false}
                onOpenChange={(value) => setOpenSections((prev) => ({ ...prev, [item.title]: value }))}
              >
                <SidebarMenuItem className="py-0.5">
                  <SidebarMenuButton
                    asChild
                    isActive={active && !hasChildren}
                    data-suppress-active={hasChildren || undefined}
                    data-active-parent={(hasChildren && active) || undefined}
                    className="bg-transparent! transition-[color]! before:absolute before:inset-0 before:-z-[1] before:rounded-md before:bg-sidebar-accent before:opacity-0 before:transition-opacity before:duration-150 before:pointer-events-none before:content-[''] after:absolute after:inset-0 after:-z-[2] after:rounded-md after:bg-gray-100 dark:after:bg-gray-800 after:opacity-0 after:transition-opacity after:duration-150 after:pointer-events-none after:content-[''] group-hover/menu-item:after:opacity-100 data-[active-parent=true]:group-hover/menu-item:after:opacity-0 data-[active=true]:before:opacity-100 hover:[&>span]:text-gray-900 dark:hover:[&>span]:text-sidebar-foreground hover:[&_svg]:[--icon-color:var(--color-gray-700)]! dark:hover:[&_svg]:[--icon-color:var(--color-sidebar-foreground)]! data-[active=true]:hover:[&>span]:text-sidebar-accent-foreground! data-[active=true]:hover:[&_svg]:[--icon-color:var(--color-sidebar-accent-foreground)]! [&>span]:transition-colors [&>span]:duration-150 [&_svg_path]:transition-[stroke,fill] [&_svg_path]:duration-150"
                  >
                    <NavLink to={item.path} onClick={closeMobileSidebar}>
                      {item.icon}
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                  {hasChildren && (
                    <>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction className="peer-data-[size=default]/menu-button:top-2 [&>svg]:transition-transform [&>svg]:duration-200 [&>svg]:ease-out data-[state=open]:[&>svg]:rotate-90">
                          <ChevronRight className="size-4" />
                          <span className="sr-only">Toggle</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub className="gap-0">
                          {item.items!.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.title} className="py-0.5">
                              <SidebarMenuSubButton
                                asChild
                                isActive={isSubItemActive(location.pathname, subItem)}
                                className="bg-transparent! transition-[color]! before:absolute before:inset-0 before:-z-[1] before:rounded-md before:bg-sidebar-accent before:opacity-0 before:transition-opacity before:duration-150 before:pointer-events-none before:content-[''] after:absolute after:inset-0 after:-z-[2] after:rounded-md after:bg-gray-100 dark:after:bg-gray-800 after:opacity-0 after:transition-opacity after:duration-150 after:pointer-events-none after:content-[''] group-hover/menu-sub-item:after:opacity-100 data-[active=true]:before:opacity-100"
                              >
                                <NavLink to={subItem.path} onClick={closeMobileSidebar}>
                                  <span>{subItem.title}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </>
                  )}
                </SidebarMenuItem>
              </Collapsible>
              {item.separatorAfter && <SidebarSeparator className="my-2" />}
            </React.Fragment>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
};

export const NavMain = React.memo(NavMainImpl);
